import React, { useEffect, useMemo } from "react";
import { Spinner, Stack } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { setPageNameHeader } from "src/redux/actions/header";
import { getUserData } from "src/redux/selectors/user";
import {
  FirebaseDataService,
  GetBacklogAgingResponse,
  GetDashboardDataResponse,
  GetWidgetsDataResponse,
  GetWorkRequestsByPriorityResponse,
  GetWorkRequestsByTypeResponse,
} from "src/services/Firebase/data";
import { Content, colors } from "src/styles";
import { useTestGroup } from "src/hooks/useTestGroup";
import styled from "styled-components";
// import FileSharing from "../FileSharing";
// import WorkOrderStatistics from "./WorkOrderStatistics";
import WorkOrderPriorityChart from "./WorkOrderPriorityChart";
import WorkOrderTypeChart from "./WorkOrderTypeChart";
// import SpendPerMonthChart from "./SpendPerMonthChart";
// import SpendByServiceChart from "./SpendByServiceChart";
// import BudgetToActualChart from "./BudgetToActualChart";
// import ComplianceTrackerTable from "./ComplianceTracker";
// import InternallyCompletedWorkChart from "./InternallyCompletedWorkChart";
// import OpenWorkByRegionChart from "./OpenWorkByRegionChart";
import WorkOrdersLocationChart from "./WorkOrdersLocationChart";
import WorkOrderStatuses from "./WorkOrderStatuses";
import BacklogAging from "./BacklogAging";
import KpiCard from "./KpiCard";
import { PmComplianceTiles, PmServicesTable } from "./PreventativeMaintenancePanel";
import FinancialPanel from "./FinancialPanel";
import FileSharing from "../FileSharing";

interface WidgetProps {
  caption: string;
  preloadedData?: GetWidgetsDataResponse;
}

interface HomeKpiItem {
  label: string;
  value: number | string;
  sub: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  accentColor: string;
}

const BROOKFIELD_CLIENT_ID = "BROOKFIELD";
const ENBRIDGE_CLIENT_ID = "ENBRIDGE";

// const BROOKFIELD_STATIC_DATA: GetDashboardDataResponse = {
//   widgetsData: {
//     summary: {
//       totalWorkOrders: 350,
//       activeWorkOrders: 105,
//       completedWorkOrders: 245,
//       totalBacklogAging: 180,
//     },
//     workRequestsStatistics: [
//       { status: "Waiting for Approval", statusCode: "WAPPR", maxvalue: "WAPPR", count: 45 },
//       { status: "Approved", statusCode: "APPR", maxvalue: "APPR", count: 32 },
//       { status: "Work in Progress", statusCode: "WSCH", maxvalue: "WSCH", count: 28 },
//       { status: "In Progress", statusCode: "INPRG", maxvalue: "INPRG", count: 19 },
//       { status: "Completed", statusCode: "COMP", maxvalue: "COMP", count: 156 },
//       { status: "Closed", statusCode: "CLOSE", maxvalue: "CLOSE", count: 89 },
//     ],
//     workRequestsPriorityStatistics: [
//       { priorityId: 1, priorityName: "Critical", priorityColor: "#d32f2f", count: 28 },
//       { priorityId: 2, priorityName: "High", priorityColor: "#f57c00", count: 45 },
//       { priorityId: 3, priorityName: "Medium", priorityColor: "#fbc02d", count: 67 },
//       { priorityId: 4, priorityName: "Low", priorityColor: "#388e3c", count: 92 },
//     ],
//     workRequestsWorkTypeStatistics: [
//       { category: "On-Demand", count: 78 },
//       { category: "Planned", count: 154 },
//     ],
//     workRequestsVendorTypeStatistics: [
//       { category: "Internal", count: 198 },
//       { category: "External", count: 152 },
//     ],
//     workOrdersByLocation: [
//       { locationName: "Building A", count: 42 },
//       { locationName: "Building B", count: 35 },
//       { locationName: "Parking Garage", count: 28 },
//       { locationName: "Common Areas", count: 21 },
//       { locationName: "Lobby", count: 17 },
//     ],
//     backlogAging: [
//       {
//         priorityId: 1,
//         priorityName: "Critical",
//         priorityColor: "#d32f2f",
//         thresholdDays: 1,
//         exceededCount: 8,
//         workOrders: [],
//       },
//       {
//         priorityId: 2,
//         priorityName: "High",
//         priorityColor: "#f57c00",
//         thresholdDays: 3,
//         exceededCount: 15,
//         workOrders: [],
//       },
//       {
//         priorityId: 3,
//         priorityName: "Medium",
//         priorityColor: "#fbc02d",
//         thresholdDays: 7,
//         exceededCount: 23,
//         workOrders: [],
//       },
//       {
//         priorityId: 4,
//         priorityName: "Low",
//         priorityColor: "#388e3c",
//         thresholdDays: 14,
//         exceededCount: 31,
//         workOrders: [],
//       },
//     ],
//   },
// };

const getStatusLink = (statusCode: string, status: string) => {
  const closedStatuses = ["COMP", "CLOSE", "CAN"];
  if (closedStatuses.includes(statusCode)) {
    return `/workrequests?closedWorkRequestsPage=1&searchTerm=${status}`;
  }

  return `/workrequests?openedWorkRequestsPage=1&searchTerm=${status}`;
};

const WorkRequestStatistics = ({ caption, preloadedData }: WidgetProps) => {
  const data = preloadedData;

  const total = useMemo(() => {
    return (
      data?.widgetsData.workRequestsStatistics
        .filter((item) => item.statusCode !== "COMP")
        .reduce((acc, item) => acc + item.count, 0) ?? 0
    );
  }, [data]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <CardTitle>{caption}</CardTitle>
        </HeaderRow>
        {data?.widgetsData?.workRequestsStatistics ? (
          <Grid>
            <GridHeader>Status</GridHeader>
            <GridHeader>Count</GridHeader>
            <GridHeader>Progress</GridHeader>
            {data.widgetsData.workRequestsStatistics
              .filter((item) => item.statusCode !== "COMP")
              .map((item) => (
                <>
                  <GridCell>
                    <Status $maxvalue={item.maxvalue}>{item.status}</Status>
                  </GridCell>
                  <GridCell>
                    <StyledLink to={getStatusLink(item.statusCode, item.status)}>{item.count}</StyledLink>
                  </GridCell>
                  <GridCell>
                    <ProgressContainer>
                      <ProgressBar $percentage={Math.round((item.count * 100) / total)} />
                    </ProgressContainer>
                    <PercentLabel>{Math.round((item.count * 100) / total)}%</PercentLabel>
                  </GridCell>
                </>
              ))}
            <GridFooter>Total</GridFooter>
            <GridFooter>
              <StyledLink to="/workrequests">{total}</StyledLink>
            </GridFooter>
            <GridFooter />
          </Grid>
        ) : (
          <Spinner animation="border" />
        )}
      </Stack>
    </WidgetContainer>
  );
};

const Home = () => {
  const dispatch = useDispatch();
  const userData = useSelector(getUserData);
  const { id: loggedInUserId, isManager, clientId } = userData;
  console.log("User data in Home component:", userData);

  const isTestGroup = useTestGroup(userData.email);
  const isBrookfieldClient = (clientId ?? "").trim().toUpperCase() === BROOKFIELD_CLIENT_ID;
  const isEnbridgeClient = (clientId ?? "").trim().toUpperCase() === ENBRIDGE_CLIENT_ID;
  const isCityOfMarkhamClient = (clientId ?? "").trim().toUpperCase() === "CITYMARKHAM";

  // Single aggregated fetch — replaces 5 individual widget calls
  const [dashboardData, setDashboardData] = React.useState<GetDashboardDataResponse | undefined>();

  useEffect(() => {
    dispatch(setPageNameHeader("Home"));
    return () => {
      dispatch(setPageNameHeader(""));
    };
  }, []);

  useEffect(() => {
    if (!isTestGroup) return;

    // if (isBrookfieldClient) {
    //   setDashboardData(BROOKFIELD_STATIC_DATA);
    //   return;
    // }

    const fetchDashboard = async () => {
      const { data } = await FirebaseDataService.getDashboardData(loggedInUserId, isManager);
      setDashboardData(data);
    };

    fetchDashboard();
  }, [isTestGroup, isBrookfieldClient, loggedInUserId, isManager]);

  // Slice the unified response into per-widget shapes — memoized so child D3 effects don't re-fire on every render
  const widgetsData = useMemo<GetWidgetsDataResponse | undefined>(
    () =>
      dashboardData
        ? {
            widgetsData: {
              workRequestsStatistics: dashboardData.widgetsData.workRequestsStatistics,
              workRequestsVendorTypeStatistics: dashboardData.widgetsData.workRequestsVendorTypeStatistics,
            },
          }
        : undefined,
    [dashboardData],
  );

  const priorityData = useMemo<GetWorkRequestsByPriorityResponse | undefined>(
    () =>
      dashboardData
        ? { widgetsData: { workRequestsPriorityStatistics: dashboardData.widgetsData.workRequestsPriorityStatistics } }
        : undefined,
    [dashboardData],
  );

  const typeData = useMemo<GetWorkRequestsByTypeResponse | undefined>(
    () =>
      dashboardData
        ? { widgetsData: { workRequestsWorkTypeStatistics: dashboardData.widgetsData.workRequestsWorkTypeStatistics } }
        : undefined,
    [dashboardData],
  );

  const locationData = useMemo<
    { widgetsData: { workOrdersByLocation: { locationName: string; count: number }[] } } | undefined
  >(
    () =>
      dashboardData
        ? { widgetsData: { workOrdersByLocation: dashboardData.widgetsData.workOrdersByLocation } }
        : undefined,
    [dashboardData],
  );

  const backlogData = useMemo<GetBacklogAgingResponse | undefined>(
    () => (dashboardData ? { widgetsData: { backlogAging: dashboardData.widgetsData.backlogAging } } : undefined),
    [dashboardData],
  );

  const summary = dashboardData?.widgetsData?.summary;

  const kpis: HomeKpiItem[] = [
    {
      label: "Total orders",
      value: summary?.totalWorkOrders ?? 0,
      sub: "all statuses combined",
      icon: "ti-clipboard-list",
      iconBg: "#EAF3DE",
      iconColor: "#3B6D11",
      accentColor: "#639922",
    },
    {
      label: "Active",
      value: summary?.activeWorkOrders ?? 0,
      sub: "awaiting action",
      icon: "ti-tools",
      iconBg: "#E6F1FB",
      iconColor: "#185FA5",
      accentColor: "#378ADD",
    },
    {
      label: isEnbridgeClient ? "BUDGET TO ACTUAL" : "Completed",
      value: isEnbridgeClient ? "$638k" : (summary?.completedWorkOrders ?? 0),
      sub: isEnbridgeClient
        ? "$5.78M year-to-date spend"
        : `${summary ? Math.round((summary.completedWorkOrders / summary.totalWorkOrders) * 100) : 0}% completion rate`,
      icon: "ti-circle-check",
      iconBg: "#E1F5EE",
      iconColor: "#0F6E56",
      accentColor: "#1D9E75",
    },
    {
      label: "Backlog aging",
      value: summary?.totalBacklogAging ?? 0,
      sub: "exceeded SLA",
      icon: "ti-alert-triangle",
      iconBg: "#FCEBEB",
      iconColor: "#A32D2D",
      accentColor: "#E24B4A",
    },
  ];

  return (
    <>
      {isCityOfMarkhamClient ? (
        <FileSharing />
      ) : (
        <Content>
          {/* Row 1 — KPI summary */}
          <KpiRow>
            {kpis.map((kpi) => (
              <KpiCard key={kpi.label} {...kpi} />
            ))}
          </KpiRow>

          {/* Row 2 — Statistics table + Status & Type charts */}
          {/* <DashRow style={{ marginTop: 12, gridTemplateColumns: "1.15fr 0.85fr" }}> */}
          <DashRow style={{ marginTop: 12, gridTemplateColumns: "1.275fr 0.625fr" }}>
            <WorkRequestStatistics caption="Work request statistics" preloadedData={widgetsData} />
            <MidRight>
              {!isBrookfieldClient && <WorkOrderStatuses caption="Internal vs. External" preloadedData={widgetsData} />}
              <WorkOrderTypeChart caption="On-Demand vs. Planned" preloadedData={typeData} />
            </MidRight>
          </DashRow>

          {/* Row 3 — Priority, Location, Backlog */}
          <DashRow style={{ marginTop: 12 }}>
            <WorkOrderPriorityChart caption="By priority" preloadedData={priorityData} />
            <WorkOrdersLocationChart caption="Work orders by location" preloadedData={locationData} />
            <BacklogAging caption="Backlog aging" preloadedData={backlogData} />
          </DashRow>

          {/* Row 4 — Preventative Maintenance */}
          {!isBrookfieldClient && (
            <>
              <SectionTitle>Preventative maintenance</SectionTitle>
              <DashRow style={{ marginTop: 12, gridTemplateColumns: "0.625fr 1.275fr" }}>
                <PmComplianceTiles />
                <PmServicesTable />
              </DashRow>
            </>
          )}

          {/* Row 5 — Financial */}
          {isEnbridgeClient && (
            <>
              <SectionTitle>Financial</SectionTitle>
              <div style={{ marginTop: 12 }}>
                <FinancialPanel />
              </div>
            </>
          )}
        </Content>
      )}
    </>
  );
};

export default Home;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
`;

const DashRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.grey.dark};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 10px 0;
`;

const MidRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  & > * {
    flex: 1;
  }
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const WidgetContainer = styled.div`
  display: flex;
  height: 100%;
  background: #ffffff;
  border: 0.5px solid ${colors.grey.light11};
  border-radius: 12px;
  padding: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto 38px 1fr;
  gap: 0;
`;

const GridHeader = styled.div`
  border-bottom: 0.5px solid ${colors.grey.light11};
  padding: 0 8px 7px 0;
  font-size: 10px;
  color: ${colors.grey.light10};
`;

const GridFooter = styled.div`
  border-top: 0.5px solid ${colors.grey.light11};
  padding: 8px 8px 0 0;
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  margin-top: 4px;
`;

const GridCell = styled.div`
  padding: 5px 8px 5px 0;
  font-size: 12px;
  display: flex;
  align-items: center;
  border-bottom: 0.5px solid ${colors.grey.light11};
`;

const statusColors: { [key: string]: { bg: string; color: string } } = {
  WAPPR: { bg: "#F1EFE8", color: "#5F5E5A" },
  APPR: { bg: "#E6F1FB", color: "#185FA5" },
  WSCH: { bg: "#EAF3DE", color: "#3B6D11" },
  WMATL: { bg: "#EAF3DE", color: "#3B6D11" },
  INPRG: { bg: "#EAF3DE", color: "#3B6D11" },
  COMP: { bg: "#EAF3DE", color: "#27500A" },
  CLOSE: { bg: "#F1EFE8", color: "#444441" },
  CAN: { bg: "#FCEBEB", color: "#A32D2D" },
};

const Status = styled.div<{ $maxvalue: string }>`
  background-color: ${({ $maxvalue }) => statusColors[$maxvalue]?.bg ?? "#F1EFE8"};
  color: ${({ $maxvalue }) => statusColors[$maxvalue]?.color ?? "#5F5E5A"};
  border-radius: 20px;
  padding: 2px 9px;
  display: inline-block;
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 110px;
`;

const ProgressContainer = styled.div`
  flex: 1;
  height: 5px;
  background-color: ${colors.grey.light3};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ $percentage?: number }>`
  width: ${({ $percentage }) => $percentage}%;
  height: 100%;
  background-color: ${colors.kleenway.green};
  border-radius: 4px;
`;

const PercentLabel = styled.span`
  font-size: 10px;
  color: ${colors.grey.light10};
  min-width: 28px;
  text-align: right;
`;

const StyledLink = styled(Link)`
  font-size: 12px;
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
